"use client";

import { api } from "@/convex/_generated/api";
import { clearAuthToken, getAuthToken, setAuthToken } from "@/lib/auth-client";
import { hasFoodCreatorWebAccess } from "@/lib/auth/access";
import {
    readOpaqueLocalStorageJson,
    writeOpaqueLocalStorageJson,
} from "@/lib/security/opaque-storage";
import { useWebAuth } from "@/lib/web-auth";
import type { Id } from "@/convex/_generated/dataModel";
import { withPublicDisplayName } from "@shared/utils/format";
import { useConvex, useQuery } from "convex/react";
import { redirectToFoodCreatorSignIn } from "@/lib/food-creator-navigation";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type FoodCreatorAuthUser = {
    _id: Id<"users">;
    userId?: Id<"users"> | string;
    name?: string | null;
    fullName?: string | null;
    email?: string;
    roles?: string[];
    isNewUser?: boolean;
    [key: string]: unknown;
};

type FoodCreatorLoginUserData = {
    _id?: Id<"users"> | string;
    userId?: Id<"users"> | string;
    name?: string | null;
    fullName?: string | null;
    email?: string;
    roles?: string[];
    isNewUser?: boolean;
    [key: string]: unknown;
};

type FoodCreatorAuthProfile = {
    _id: Id<"foodCreators">;
    userId?: Id<"users"> | string;
    name?: string;
    email?: string;
    currency?: string | null;
    bio?: string;
    specialties?: string[];
    address?: string;
    location?: {
        city?: string;
        coordinates?: number[];
    };
    profileImage?: string;
    featuredVideoId?: Id<"videoPosts"> | string;
    complianceTrainingSkipped?: boolean;
    accountType?: "content_creator" | "taste_creator" | "both";
    adTier?: string;
    creatorLevel?: string;
    verificationBadgeLevel?: string;
    deliveryTime?: string | number | null;
    distance?: string | number;
    foodCreatorType?: string;
    isAvailable?: boolean;
    rating?: number;
    status?: string;
    user_phone?: string;
    _creationTime?: number;
    [key: string]: unknown;
};

type FoodCreatorSessionState = {
    user?: unknown;
    foodCreator?: unknown;
    isBasicOnboardingComplete?: unknown;
    isOnboardingComplete?: unknown;
};

interface FoodCreatorAuthContextType {
    foodCreator: FoodCreatorAuthProfile | null;
    user: FoodCreatorAuthUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    hasValidatedSession: boolean;
    isBasicOnboardingComplete: boolean;
    isOnboardingComplete: boolean;
    sessionToken: string | null;
    login: (token: string, user: FoodCreatorLoginUserData | null | undefined) => Promise<void>;
    refreshFoodCreator: () => Promise<void>;
    logout: () => Promise<void>;
}

const FoodCreatorAuthContext = createContext<FoodCreatorAuthContextType | undefined>(undefined);

interface FoodCreatorAuthProviderProps {
    children: ReactNode;
}

const FOOD_CREATOR_WEB_CACHED_USER_KEY = "cribnosh_food_creator_cached_user";
const FOOD_CREATOR_WEB_CACHED_PROFILE_KEY = "cribnosh_food_creator_cached_profile";
const INVALID_SESSION_CLEAR_GRACE_MS = 2 * 60 * 1000;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function readStringProperty(value: unknown, key: string): string | undefined {
    if (!isRecord(value)) return undefined;
    const property = value[key];
    return typeof property === "string" ? property : undefined;
}

function readBooleanProperty(value: unknown, key: string): boolean | undefined {
    if (!isRecord(value)) return undefined;
    const property = value[key];
    return typeof property === "boolean" ? property : undefined;
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isFoodCreatorLoginUserData(value: unknown): value is FoodCreatorLoginUserData {
    if (!isRecord(value)) return false;
    const id = value._id ?? value.userId;
    if (id !== undefined && typeof id !== "string") return false;
    if (value.name !== undefined && typeof value.name !== "string" && value.name !== null) return false;
    if (value.fullName !== undefined && typeof value.fullName !== "string" && value.fullName !== null) return false;
    if (value.email !== undefined && typeof value.email !== "string") return false;
    if (value.roles !== undefined && !isStringArray(value.roles)) return false;
    if (value.isNewUser !== undefined && typeof value.isNewUser !== "boolean") return false;
    return true;
}

function toFoodCreatorAuthUser(value: unknown): FoodCreatorAuthUser | null | undefined {
    if (value === undefined) return undefined;
    if (value === null || !isFoodCreatorLoginUserData(value)) return null;

    const id = value._id ?? value.userId;
    if (typeof id !== "string" || !id) return null;

    return {
        ...value,
        _id: id as Id<"users">,
    };
}

function isFoodCreatorAuthProfile(value: unknown): value is FoodCreatorAuthProfile {
    if (!isRecord(value)) return false;
    if (typeof value._id !== "string" || !value._id) return false;
    if (value.userId !== undefined && typeof value.userId !== "string") return false;
    if (value.name !== undefined && typeof value.name !== "string") return false;
    if (value.email !== undefined && typeof value.email !== "string") return false;
    if (value.bio !== undefined && typeof value.bio !== "string") return false;
    if (value.specialties !== undefined && !isStringArray(value.specialties)) return false;
    if (value.address !== undefined && typeof value.address !== "string") return false;
    if (value.profileImage !== undefined && typeof value.profileImage !== "string") return false;
    if (value.featuredVideoId !== undefined && typeof value.featuredVideoId !== "string") return false;
    if (value.complianceTrainingSkipped !== undefined && typeof value.complianceTrainingSkipped !== "boolean") return false;
    if (value.rating !== undefined && typeof value.rating !== "number") return false;
    if (value._creationTime !== undefined && typeof value._creationTime !== "number") return false;
    return true;
}

function toFoodCreatorAuthProfile(value: unknown): FoodCreatorAuthProfile | null | undefined {
    if (value === undefined) return undefined;
    if (value === null || !isFoodCreatorAuthProfile(value)) return null;
    return value;
}

function normalizeUserForDisplay(user: FoodCreatorAuthUser | null | undefined) {
    return withPublicDisplayName(user, { fallback: "User" });
}

function readCachedJson<T>(key: string, isValue: (value: unknown) => value is T): T | null {
    return readOpaqueLocalStorageJson(key, isValue);
}

function writeCachedJson(key: string, value: unknown | null): void {
    writeOpaqueLocalStorageJson(key, value);
}

export function FoodCreatorAuthProvider({ children }: FoodCreatorAuthProviderProps) {
    const convex = useConvex();
    const {
        user: sharedSessionUser,
        sessionToken: sharedSessionToken,
        isLoading: isWebAuthLoading,
        hasFoodCreatorAccess: sharedHasFoodCreatorAccess,
        clearSession,
    } = useWebAuth();
    const [sessionToken, setSessionToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [didQueryTimeout, setDidQueryTimeout] = useState(false);
    const [confirmedUser, setConfirmedUser] = useState<FoodCreatorAuthUser | null>(null);
    const [persistedUser, setPersistedUser] = useState<FoodCreatorAuthUser | null>(null);
    const [persistedFoodCreator, setPersistedFoodCreator] = useState<FoodCreatorAuthProfile | null>(null);
    const [cookieFallbackState, setCookieFallbackState] = useState<FoodCreatorSessionState | null>(null);
    const [isCookieFallbackLoading, setIsCookieFallbackLoading] = useState(false);
    const [fallbackRefreshNonce, setFallbackRefreshNonce] = useState(0);
    const lastNullValidationRef = useRef<{ token: string; timestamp: number } | null>(null);
    const pendingInvalidSessionRef = useRef<{ token: string; firstConfirmedInvalidAt: number } | null>(null);

    const clearInvalidFoodCreatorSession = useCallback(() => {
        pendingInvalidSessionRef.current = null;
        clearSession();
        writeCachedJson(FOOD_CREATOR_WEB_CACHED_USER_KEY, null);
        writeCachedJson(FOOD_CREATOR_WEB_CACHED_PROFILE_KEY, null);
        setSessionToken(null);
        setConfirmedUser(null);
        setPersistedUser(null);
        setPersistedFoodCreator(null);
        setCookieFallbackState(null);
        setIsCookieFallbackLoading(false);
        setIsLoading(false);
    }, [clearSession]);

    const isLikelyNetworkError = useCallback((error: unknown): boolean => {
        if (!error) return false;
        const message =
            error instanceof Error
                ? error.message
                : String(
                    readStringProperty(error, "message") ||
                    readStringProperty(isRecord(error) ? error.data : undefined, "message") ||
                    error
                );
        const lower = message.toLowerCase();
        const indicators = [
            "network request failed",
            "network error",
            "failed to fetch",
            "fetch failed",
            "timeout",
            "offline",
            "connection",
            "econnrefused",
            "etimedout",
        ];
        return indicators.some((indicator) => lower.includes(indicator));
    }, []);

    useEffect(() => {
        setSessionToken((currentToken) =>
            currentToken === sharedSessionToken ? currentToken : sharedSessionToken,
        );
    }, [sharedSessionToken]);

    // Load session token from auth-client on mount
    useEffect(() => {
        const loadToken = () => {
            try {
                const token = getAuthToken();
                const cachedUser = readCachedJson(FOOD_CREATOR_WEB_CACHED_USER_KEY, isFoodCreatorLoginUserData);
                const cachedFoodCreator = readCachedJson(FOOD_CREATOR_WEB_CACHED_PROFILE_KEY, isFoodCreatorAuthProfile);
                setSessionToken(token);
                if (token && cachedUser) {
                    setPersistedUser(normalizeUserForDisplay(toFoodCreatorAuthUser(cachedUser)) ?? null);
                }
                if (token && cachedFoodCreator) {
                    setPersistedFoodCreator(cachedFoodCreator);
                }
                if (!token) {
                    writeCachedJson(FOOD_CREATOR_WEB_CACHED_USER_KEY, null);
                    writeCachedJson(FOOD_CREATOR_WEB_CACHED_PROFILE_KEY, null);
                    setConfirmedUser(null);
                }
            } catch (error) {
                console.error("Error loading session token:", error);
                setSessionToken(null);
                setConfirmedUser(null);
            } finally {
                setIsLoading(false);
            }
        };
        loadToken();
    }, []);

    // Get user from session token
    const userQueryResult = useQuery(
        api.queries.users.getUserBySessionToken,
        sessionToken ? { sessionToken } : "skip"
    );

    const user = useMemo(
        () => normalizeUserForDisplay(toFoodCreatorAuthUser(userQueryResult)),
        [userQueryResult],
    );
    const normalizedSharedSessionUser = useMemo(
        () => normalizeUserForDisplay(toFoodCreatorAuthUser(sharedSessionUser)) ?? null,
        [sharedSessionUser],
    );
    const fallbackUser = useMemo(
        () => normalizeUserForDisplay(toFoodCreatorAuthUser(cookieFallbackState?.user)) ?? null,
        [cookieFallbackState?.user],
    );
    const validatedUser = user ?? fallbackUser ?? confirmedUser;
    const effectiveUser = validatedUser ?? persistedUser;

    // Get foodCreator profile if user has foodCreator role
    const foodCreatorQueryResult = useQuery(
        api.queries.foodCreators.getByUserId,
        effectiveUser && hasFoodCreatorWebAccess(effectiveUser) && effectiveUser._id && sessionToken
            ? { userId: effectiveUser._id, sessionToken: sessionToken }
            : "skip"
    );

    const foodCreator = toFoodCreatorAuthProfile(foodCreatorQueryResult);
    const fallbackFoodCreator = useMemo(
        () => toFoodCreatorAuthProfile(cookieFallbackState?.foodCreator) ?? null,
        [cookieFallbackState?.foodCreator],
    );
    const effectiveFoodCreator = foodCreator ?? fallbackFoodCreator ?? persistedFoodCreator;

    // Check if basic onboarding is complete (profile setup)
    const basicOnboardingQueryResult = useQuery(
        api.queries.foodCreators.isBasicOnboardingComplete,
        effectiveFoodCreator?._id && sessionToken
            ? { foodCreatorId: effectiveFoodCreator._id, sessionToken }
            : "skip"
    );
    const fallbackIsBasicOnboardingComplete =
        readBooleanProperty(cookieFallbackState, "isBasicOnboardingComplete") ?? false;
    const isBasicOnboardingComplete = basicOnboardingQueryResult ?? fallbackIsBasicOnboardingComplete;

    // Check if onboarding is complete (compliance course completed)
    const onboardingCompleteQueryResult = useQuery(
        api.queries.foodCreatorCourses.isOnboardingComplete,
        effectiveFoodCreator?._id && sessionToken
            ? { foodCreatorId: effectiveFoodCreator._id, sessionToken }
            : "skip"
    );
    const fallbackIsOnboardingComplete =
        readBooleanProperty(cookieFallbackState, "isOnboardingComplete") ?? false;
    const isOnboardingComplete = onboardingCompleteQueryResult ?? fallbackIsOnboardingComplete;

    const isFallbackMode = !sessionToken && !!normalizedSharedSessionUser && sharedHasFoodCreatorAccess;

    // Check if user is authenticated as a foodCreator
    const isAuthenticated =
        !!effectiveUser && hasFoodCreatorWebAccess(effectiveUser);
    const hasValidatedSession =
        !!validatedUser && hasFoodCreatorWebAccess(validatedUser);
    const isFoodCreatorProfileLoading =
        !!effectiveUser &&
        hasFoodCreatorWebAccess(effectiveUser) &&
        !effectiveFoodCreator &&
        (
            (sessionToken && foodCreatorQueryResult === undefined) ||
            (isFallbackMode && isCookieFallbackLoading)
        );
    const isBasicOnboardingLoading =
        !!effectiveFoodCreator?._id &&
        (
            (sessionToken && basicOnboardingQueryResult === undefined) ||
            (isFallbackMode && isCookieFallbackLoading)
        );
    const isOnboardingCompleteLoading =
        !!effectiveFoodCreator?._id &&
        (
            (sessionToken && onboardingCompleteQueryResult === undefined) ||
            (isFallbackMode && isCookieFallbackLoading)
        );

    useEffect(() => {
        if (sessionToken === null && !normalizedSharedSessionUser) {
            setConfirmedUser(null);
        }
    }, [normalizedSharedSessionUser, sessionToken]);

    useEffect(() => {
        if (normalizedSharedSessionUser && sharedHasFoodCreatorAccess) {
            setConfirmedUser(normalizedSharedSessionUser);
            setPersistedUser(normalizedSharedSessionUser);
            writeCachedJson(FOOD_CREATOR_WEB_CACHED_USER_KEY, normalizedSharedSessionUser);
        }
    }, [normalizedSharedSessionUser, sharedHasFoodCreatorAccess]);

    useEffect(() => {
        if (sessionToken || !normalizedSharedSessionUser || !sharedHasFoodCreatorAccess) {
            setIsCookieFallbackLoading(false);
            return;
        }

        let cancelled = false;
        setIsCookieFallbackLoading(true);

        const loadCookieFallbackState = async () => {
            try {
                const response = await fetch("/api/food-creators/session-state", {
                    method: "GET",
                    cache: "no-store",
                    credentials: "same-origin",
                });

                if (cancelled) return;

                if (response.status === 401) {
                    clearInvalidFoodCreatorSession();
                    return;
                }

                const json = (await response.json().catch(() => null)) as
                    | { ok?: unknown; result?: FoodCreatorSessionState }
                    | null;
                if (!response.ok || !json?.ok || !isRecord(json.result)) {
                    throw new Error("Failed to load fallback food creator session state");
                }

                setCookieFallbackState(json.result);

                const fallbackResolvedUser =
                    normalizeUserForDisplay(toFoodCreatorAuthUser(json.result.user)) ?? null;
                if (fallbackResolvedUser) {
                    setConfirmedUser(fallbackResolvedUser);
                    setPersistedUser(fallbackResolvedUser);
                    writeCachedJson(FOOD_CREATOR_WEB_CACHED_USER_KEY, fallbackResolvedUser);
                }

                const fallbackResolvedFoodCreator =
                    toFoodCreatorAuthProfile(json.result.foodCreator) ?? null;
                if (fallbackResolvedFoodCreator) {
                    setPersistedFoodCreator(fallbackResolvedFoodCreator);
                    writeCachedJson(FOOD_CREATOR_WEB_CACHED_PROFILE_KEY, fallbackResolvedFoodCreator);
                }
            } catch (error) {
                if (!cancelled) {
                    console.warn("FoodCreatorAuth: Failed to load cookie session fallback", error);
                }
            } finally {
                if (!cancelled) {
                    setIsCookieFallbackLoading(false);
                }
            }
        };

        void loadCookieFallbackState();
        return () => {
            cancelled = true;
        };
    }, [
        clearInvalidFoodCreatorSession,
        fallbackRefreshNonce,
        normalizedSharedSessionUser,
        sessionToken,
        sharedHasFoodCreatorAccess,
    ]);

    useEffect(() => {
        if (
            !isWebAuthLoading &&
            sharedSessionUser &&
            !sharedHasFoodCreatorAccess
        ) {
            console.warn(
                "FoodCreatorAuth: Shared web auth session is not food creator authorized, clearing token",
            );
            clearInvalidFoodCreatorSession();
        }
    }, [
        clearInvalidFoodCreatorSession,
        isWebAuthLoading,
        sharedHasFoodCreatorAccess,
        sharedSessionUser,
    ]);

    useEffect(() => {
        if (!sessionToken || user === undefined) {
            return;
        }

        if (user && !hasFoodCreatorWebAccess(user)) {
            console.warn(
                "FoodCreatorAuth: Session resolved without food creator access, clearing token",
            );
            clearInvalidFoodCreatorSession();
        }
    }, [clearInvalidFoodCreatorSession, sessionToken, user]);

    // Persist the most recent valid user so transient network failures don't force
    // a signed-out state in the UI.
    useEffect(() => {
        if (user && sessionToken) {
            setConfirmedUser(user);
            setPersistedUser(user);
            writeCachedJson(FOOD_CREATOR_WEB_CACHED_USER_KEY, user);
        }
    }, [user, sessionToken]);

    useEffect(() => {
        if (fallbackUser) {
            setConfirmedUser(fallbackUser);
            setPersistedUser(fallbackUser);
            writeCachedJson(FOOD_CREATOR_WEB_CACHED_USER_KEY, fallbackUser);
        }
    }, [fallbackUser]);

    useEffect(() => {
        if (foodCreator && sessionToken) {
            setPersistedFoodCreator(foodCreator);
            writeCachedJson(FOOD_CREATOR_WEB_CACHED_PROFILE_KEY, foodCreator);
        }
    }, [foodCreator, sessionToken]);

    useEffect(() => {
        if (fallbackFoodCreator) {
            setPersistedFoodCreator(fallbackFoodCreator);
            writeCachedJson(FOOD_CREATOR_WEB_CACHED_PROFILE_KEY, fallbackFoodCreator);
        }
    }, [fallbackFoodCreator]);

    // Reset pending invalid markers whenever session rotates or user recovers.
    useEffect(() => {
        if (!sessionToken || user || fallbackUser) {
            pendingInvalidSessionRef.current = null;
        }
    }, [fallbackUser, sessionToken, user]);

    // Verify null-user states before clearing session token.
    useEffect(() => {
        if (user !== null || !sessionToken) {
            return;
        }

        const now = Date.now();
        const lastValidation = lastNullValidationRef.current;
        if (
            lastValidation &&
            lastValidation.token === sessionToken &&
            now - lastValidation.timestamp < 30000
        ) {
            return;
        }
        lastNullValidationRef.current = { token: sessionToken, timestamp: now };

        let cancelled = false;
        const confirmSessionBeforeClearing = async () => {
            try {
                const confirmedUser = await convex.query(api.queries.users.getUserBySessionToken, {
                    sessionToken,
                });

                if (cancelled) return;

                if (confirmedUser) {
                    pendingInvalidSessionRef.current = null;
                    console.warn(
                        "FoodCreatorAuth: Recovered user after transient auth query failure, preserving session"
                    );
                    const normalizedUser =
                        normalizeUserForDisplay(toFoodCreatorAuthUser(confirmedUser)) ?? null;
                    setConfirmedUser(normalizedUser);
                    setPersistedUser(normalizedUser);
                    setIsLoading(false);
                    return;
                }

                const now = Date.now();
                const pendingInvalid = pendingInvalidSessionRef.current;
                if (!pendingInvalid || pendingInvalid.token !== sessionToken) {
                    pendingInvalidSessionRef.current = {
                        token: sessionToken,
                        firstConfirmedInvalidAt: now,
                    };
                    console.warn(
                        `FoodCreatorAuth: Session reported invalid; waiting ${INVALID_SESSION_CLEAR_GRACE_MS}ms before clearing`
                    );
                    setIsLoading(false);
                    return;
                }

                if (now - pendingInvalid.firstConfirmedInvalidAt < INVALID_SESSION_CLEAR_GRACE_MS) {
                    setIsLoading(false);
                    return;
                }

                const reconfirmedUser = await convex.query(api.queries.users.getUserBySessionToken, {
                    sessionToken,
                });

                if (cancelled) return;

                if (reconfirmedUser) {
                    pendingInvalidSessionRef.current = null;
                    console.warn(
                        "FoodCreatorAuth: Session recovered during grace-period recheck, preserving session"
                    );
                    const normalizedUser =
                        normalizeUserForDisplay(toFoodCreatorAuthUser(reconfirmedUser)) ?? null;
                    setConfirmedUser(normalizedUser);
                    setPersistedUser(normalizedUser);
                    setIsLoading(false);
                    return;
                }

                console.warn("FoodCreatorAuth: Session confirmed invalid after grace-period recheck, clearing token");
                pendingInvalidSessionRef.current = null;
                if (!cancelled) {
                    clearInvalidFoodCreatorSession();
                }
            } catch (error) {
                if (cancelled) return;
                if (isLikelyNetworkError(error)) {
                    console.warn(
                        "FoodCreatorAuth: Network issue while validating session, keeping existing session"
                    );
                } else {
                    console.warn(
                        "FoodCreatorAuth: Could not confirm invalid session; preserving token to avoid accidental sign-out",
                        error
                    );
                }
                setIsLoading(false);
            }
        };

        void confirmSessionBeforeClearing();
        return () => {
            cancelled = true;
        };
    }, [clearInvalidFoodCreatorSession, convex, isLikelyNetworkError, sessionToken, user]);

    // Determine if we're still loading
    const isSessionTokenQueryLoading =
        sessionToken !== null && user === undefined && !persistedUser;
    const isFallbackQueryLoading =
        isFallbackMode && isCookieFallbackLoading && !persistedUser && !fallbackUser;
    const isQueryLoading = isSessionTokenQueryLoading || isFallbackQueryLoading;

    // Safety timeout for query loading
    useEffect(() => {
        if (isQueryLoading) {
            const timeout = setTimeout(() => {
                console.warn("FoodCreatorAuth: Query loading timeout - stopping loading");
                setDidQueryTimeout(true);
                setIsLoading(false);
            }, 10000); // 10 second timeout

            return () => clearTimeout(timeout);
        }

        setDidQueryTimeout(false);
    }, [isQueryLoading]);

    const login = useCallback(
        async (token: string, userData: FoodCreatorLoginUserData | null | undefined) => {
            try {
                // Store session token using auth-client
                await setAuthToken(token);
                const authUser = toFoodCreatorAuthUser(userData);
                if (authUser) {
                    const displayUser = normalizeUserForDisplay(authUser);
                    if (displayUser) {
                        setPersistedUser(displayUser);
                        writeCachedJson(FOOD_CREATOR_WEB_CACHED_USER_KEY, displayUser);
                    }
                }
                // Update state to trigger re-fetch
                setSessionToken(getAuthToken());
                setFallbackRefreshNonce((current) => current + 1);
            } catch (error) {
                console.error("Error during login:", error);
                throw error;
            }
        },
        []
    );

    const refreshFoodCreator = useCallback(async () => {
        // Force refetch by updating session token state
        const token = getAuthToken();
        setSessionToken(token);
        setFallbackRefreshNonce((current) => current + 1);
    }, []);

    const logout = useCallback(async () => {
        try {
            await fetch("/api/session/logout", {
                method: "POST",
                credentials: "same-origin",
            });
        } catch (error) {
            console.error("Logout error in FoodCreatorAuth:", error);
        } finally {
            // Clear session token using auth-client regardless of error.
            clearAuthToken();
            setSessionToken(null);
            setConfirmedUser(null);
            setPersistedUser(null);
            setPersistedFoodCreator(null);
            setCookieFallbackState(null);
            setIsCookieFallbackLoading(false);
            writeCachedJson(FOOD_CREATOR_WEB_CACHED_USER_KEY, null);
            writeCachedJson(FOOD_CREATOR_WEB_CACHED_PROFILE_KEY, null);
            redirectToFoodCreatorSignIn();
        }
    }, []);

    const contextValue = useMemo<FoodCreatorAuthContextType>(
        () => ({
            foodCreator: effectiveFoodCreator || null,
            user: effectiveUser || null,
            isLoading:
                (
                    isLoading ||
                    isWebAuthLoading ||
                    isQueryLoading ||
                    isFoodCreatorProfileLoading ||
                    isBasicOnboardingLoading ||
                    isOnboardingCompleteLoading
                ) && !didQueryTimeout,
            isAuthenticated: !!isAuthenticated,
            hasValidatedSession: !!hasValidatedSession,
            isBasicOnboardingComplete: isBasicOnboardingComplete === true,
            isOnboardingComplete: isOnboardingComplete === true,
            sessionToken,
            login,
            refreshFoodCreator,
            logout,
        }),
        [
            didQueryTimeout,
            effectiveFoodCreator,
            effectiveUser,
            hasValidatedSession,
            isAuthenticated,
            isBasicOnboardingComplete,
            isBasicOnboardingLoading,
            isFoodCreatorProfileLoading,
            isLoading,
            isOnboardingComplete,
            isOnboardingCompleteLoading,
            isQueryLoading,
            isWebAuthLoading,
            login,
            logout,
            refreshFoodCreator,
            sessionToken,
        ],
    );

    return <FoodCreatorAuthContext.Provider value={contextValue}>{children}</FoodCreatorAuthContext.Provider>;
}

export function useFoodCreatorAuth(): FoodCreatorAuthContextType {
    const context = useContext(FoodCreatorAuthContext);
    if (context === undefined) {
        throw new Error("useFoodCreatorAuth must be used within a FoodCreatorAuthProvider");
    }
    return context;
}
